import {List, Map} from 'immutable';
import moment from 'moment';
import {
  fractionToPercentage,
  rankData,
  trendFromRanks,
  trendTooltip,
} from 'components/dashboard/coaching-effectiveness/common';
import withSorting from 'components/pure/sortable-tables/withSorting';
import {
  compareNumeric,
  compareRanks,
  compareReps,
} from 'components/coaching/landing-pages/helpers/tableSorts';
import React from 'react';
import CoachingEffectivenessTableHeader from 'components/dashboard/coaching-effectiveness/CoachingEffectivenessTableHeader';
import {RepProfileAndName} from 'components/coaching/landing-pages/helpers/ProfileAndName';
import {PercentChangeIndicator} from 'components/coaching/landing-pages/helpers/PercentChangeIndicator';
import RankWithDelta from 'components/dashboard/coaching-effectiveness/RankWithDelta';
import CoachWithTimeSpent from 'components/dashboard/coaching-effectiveness/CoachWithTimeSpent';
import ManagerCoachingTimeReceived from 'components/dashboard/coaching-effectiveness/ManagerCoachingTimeReceived';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import MetricTooltip from 'components/pure/tooltips/MetricTooltip';
import './ManagerCoachingEffectivenessTable.styl';
import {TOOLTIP_DELAY} from 'components/StatsPage2.utils';

export const managerCoachingEffectvenessHeader = [
  {label: 'Rep', column: 'rep', className: 'rep', tooltipContent: ''},
  {
    label: 'Coaching Time',
    column: 'coachingTimeReceived',
    className: 'coaching-received',
    tooltipClassName: 'coaching-received-tooltip',
    tooltipContent: (
      <MetricTooltip
        header="Coaching Time"
        tooltip="The value on the left represents the total amount of coaching received by a rep from both their manager and other coaches. The value on the right represents the amount of coaching received by a rep from their manager only. How much coaching time is each rep receiving from their direct manager vs other coaches? If a rep's call scores aren't improving, consider changing the person providing the coaching. HBR research on 'connector managers' shows that coaching by committee produces better results than reps who receive coaching only from one person."
        link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
      />
    ),
  },
  {
    label: 'Rep Call Score Trend',
    column: 'scoreTrend',
    className: 'score-trend',
    tooltipClassName: 'rep-call-score-trend-tooltip',
    tooltipContent: (
      <MetricTooltip
        header="Rep Call Score Trend"
        tooltip="A decreasing Rep Call Score Trend generally indicates either that the Forgetting Curve is causing a rep to revert to old habits, or the focus of current coaching has shifted from a skill the rep has mastered to a new skill that the rep needs to improve. As you review these scores, determine which reason applies to each rep. Additionally, when a rep consistently maintains a high Rep Call Score Trend, they have achieved a level of mastery on that particular call type. At that point, consider changing the focus of your coaching to another call type."
        link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
      />
    ),
  },
  {
    label: 'Rep Call Score Rank',
    column: 'scoreRank',
    className: 'score-rank',
    tooltipContent: (
      <MetricTooltip
        header="Rep Call Score Rank"
        tooltip="Research shows that managers should invest their coaching time on average performers and new hires. The business impact from coaching these 'B players' is greater than coaching 'A players' or reps who are in the bottom 25%. While looking at the Rep Call Score Rank, which of your reps should you coach more and which should you coach less?"
        link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
      />
    ),
  },
  {
    label: 'Rep Call Activity Trend',
    column: 'activityTrend',
    className: 'activity-trend',
    tooltipContent: (
      <MetricTooltip
        header="Rep Call Activity Trend"
        tooltip="Explore how reps are trending in call activity. Drops in call activity could indicate that a rep might be losing motivation, spending too much time on non-selling activities, or focused on the wrong things. If you see an issue related to low activity, fix this first before coaching on call quality."
        link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
      />
    ),
  },
  {
    label: 'Rep Call Activity Rank',
    column: 'activityRank',
    className: 'activity-rank',
    tooltipContent: (
      <MetricTooltip
        header="Rep Call Activity Rank"
        tooltip="See how each rep stacks up on call activity. This metric is beneficial for recalibrating dial goals and target conversion rates across your team."
        link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
      />
    ),
  },
  {
    label: 'Coached By',
    column: 'coachedBy',
    className: 'coached-by',
    tooltipContent: (
      <MetricTooltip
        header="Coached By"
        tooltip="Who coached each rep during this period. The length of the blue arc outlining each coach's circle indicates the percentage of coaching the rep received from that coach. The percentage value can be seen by hovering over each circle."
      />
    ),
  },
];

export const coachCoachingEffectivenessHeader = [
  {label: 'Rep', column: 'rep', className: 'rep', tooltipContent: ''},
  {
    label: 'Hours Coached',
    column: 'coachingTimeGiven',
    className: 'coaching-received',
    tooltipClassName: 'coaching-received-tooltip',
    tooltipContent: (
      <MetricTooltip
        header="Hours Coached"
        tooltip="The value represents the total amount of coaching spent by the coach on a rep."
        link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
      />
    ),
  },
  {
    label: 'Score Trend',
    column: 'scoreTrend',
    className: 'score-trend',
    tooltipClassName: 'rep-call-score-trend-tooltip',
    tooltipContent: (
      <MetricTooltip
        header="Rep Call Score Trend"
        tooltip="A decreasing Rep Call Score Trend generally indicates either that the Forgetting Curve is causing a rep to revert to old habits, or the focus of current coaching has shifted from a skill the rep has mastered to a new skill that the rep needs to improve. As you review these scores, determine which reason applies to each rep. Additionally, when a rep consistently maintains a high Rep Call Score Trend, they have achieved a level of mastery on that particular call type. At that point, consider changing the focus of your coaching to another call type."
        link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
      />
    ),
  },
  {
    label: 'Rep Call Score Rank',
    column: 'scoreRank',
    className: 'score-rank',
    tooltipContent: (
      <MetricTooltip
        header="Rep Call Score Rank"
        tooltip="Research shows that managers should invest their coaching time on average performers and new hires. The business impact from coaching these 'B players' is greater than coaching 'A players' or reps who are in the bottom 25%. While looking at the Rep Call Score Rank, which of your reps should you coach more and which should you coach less?"
        link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
      />
    ),
  },
  {
    label: 'Rep Call Activity Trend',
    column: 'activityTrend',
    className: 'activity-trend',
    tooltipContent: (
      <MetricTooltip
        header="Rep Call Activity Trend"
        tooltip="Explore how reps are trending in call activity. Drops in call activity could indicate that a rep might be losing motivation, spending too much time on non-selling activities, or focused on the wrong things. If you see an issue related to low activity, fix this first before coaching on call quality."
        link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
      />
    ),
  },
  {
    label: 'Rep Call Activity Rank',
    column: 'activityRank',
    className: 'activity-rank',
    tooltipContent: (
      <MetricTooltip
        header="Rep Call Activity Rank"
        tooltip="See how each rep stacks up on call activity. This metric is beneficial for recalibrating dial goals and target conversion rates across your team."
        link="https://support.mediafly.com/hc/en-us/articles/13547960731163-Manager-Dashboard#h_01GVDJEB0JZB1A9V0C21HZREQX"
      />
    ),
  },
  {
    label: 'Coached By',
    column: 'coachedBy',
    className: 'coached-by',
    tooltipContent: (
      <MetricTooltip
        header="Coached By"
        tooltip="Who coached each rep during this period. The length of the blue arc outlining each coach's circle indicates the percentage of coaching the rep received from that coach. The percentage value can be seen by hovering over each circle."
      />
    ),
  },
];

class ManagerCoachingEffectivenessTable extends React.PureComponent {
  render() {
    return (
      <table className="manager-coaching-effectiveness">
        {this.renderHeader()}
        {this.renderBody()}
      </table>
    );
  }

  renderHeader() {
    const {sortDirection, sortColumn, onSort, viewForCoach} = this.props;

    const header = viewForCoach
      ? coachCoachingEffectivenessHeader
      : managerCoachingEffectvenessHeader;

    return (
      <thead>
        <tr>
          {header.map(data => (
            <th key={data.label} className={data.className}>
              <CoachingEffectivenessTableHeader
                data={data}
                onSort={onSort}
                sortDirection={sortDirection}
                sortColumn={sortColumn}
              />
            </th>
          ))}
        </tr>
      </thead>
    );
  }

  renderBody() {
    const {
      sortedData,
      rows = Infinity,
      coachId,
      managerId,
      adminId,
      fromPath,
      viewForCoach,
    } = this.props;

    return (
      <tbody>
        {sortedData.take(rows).map(data => {
          if (!data.rep) {
            // rep should be populated shortly after the missing users found their way to the UserStore
            return null;
          }

          return (
            <tr key={data.rep.userId}>
              <td className="rep">
                <RepProfileAndName
                  rep={data.rep}
                  coachId={coachId}
                  managerId={managerId}
                  adminId={adminId}
                  includeAvatar={false}
                  fromPath={fromPath}
                />
              </td>
              <td className="coaching-received">
                <ManagerCoachingTimeReceived
                  coachingTimeFromProvider={
                    viewForCoach ? data.coachingTimeGiven : data.coachingTimeReceived.manager
                  }
                  coachingTimeTotal={data.coachingTimeReceived.total}
                  providerIsCoach={viewForCoach}
                />
              </td>
              <td className="trend score-trend">
                <GenericTooltip
                  positionTop
                  delay={TOOLTIP_DELAY}
                  tooltip={trendTooltip(data.scoreTrend)}>
                  <PercentChangeIndicator avg={data.scoreTrend.trend} />
                </GenericTooltip>
              </td>
              <td className="rank score-rank">
                <RankWithDelta rank={data.scoreRank.rank} delta={data.scoreRank.delta} />
              </td>
              <td className="trend activity-trend">
                <GenericTooltip
                  positionTop
                  delay={TOOLTIP_DELAY}
                  tooltip={trendTooltip(data.activityTrend)}>
                  <PercentChangeIndicator avg={data.activityTrend.trend} />
                </GenericTooltip>
              </td>
              <td className="rank activity-rank">
                <RankWithDelta rank={data.activityRank.rank} delta={data.activityRank.delta} />
              </td>
              <td className="coached-by">
                {data.coachedBy.map(userAndDuration => (
                  <CoachWithTimeSpent
                    key={userAndDuration.user.userId}
                    user={userAndDuration.user}
                    fraction={userAndDuration.fraction}
                    percentage={userAndDuration.percentage}
                    plans={userAndDuration.plans}
                    adHocCalls={userAndDuration.adHocCalls}
                  />
                ))}
              </td>
            </tr>
          );
        })}
      </tbody>
    );
  }
}

export function gatherData(props) {
  const {widget, users, coachingPlans, coachId, managerId, includeReviewTime, includeMeetingTime} =
    props;
  if (!widget || !users || !coachingPlans) {
    return List();
  }
  return widget
    .get('report')
    .entrySeq()
    .map(([userId, userReport]) => {
      const rep = users.get(userId);
      const {
        activityRank,
        previousActivityRank,
        scorecardRank,
        previousScorecardRank,
        meetingTimeReceived,
        reviewTimeReceived,
        totalTimeReceived,
      } = userReport;

      const coachIdToPlans = {};
      const coachIdToAdHocCallsCount = {};
      coachingPlans
        .filter(plan => plan.recipient.id === userId)
        .forEach(plan => {
          const coachId = plan.coach.id;
          // Adhoc plans are treated specially -- we need to sum the number of calls coached to display as a separate
          // "plan" in the popup. As a consequence, such plans should not be listed separately in the list with the
          // not-adhoc plans.
          if (plan.adHoc) {
            const currentCount = coachIdToAdHocCallsCount[coachId] || 0;
            coachIdToAdHocCallsCount[coachId] =
              currentCount +
              plan.reviewFeedback.filter(feedback => feedback.reviewFileuploadId).size;
          } else {
            const list = coachIdToPlans[coachId] || [];
            list.push(plan);
            coachIdToPlans[coachId] = list;
          }
        });

      let coachingTimeMap = Map();
      if (includeReviewTime) {
        coachingTimeMap = includeMeetingTime ? totalTimeReceived : reviewTimeReceived;
      } else if (includeMeetingTime) {
        coachingTimeMap = meetingTimeReceived;
      }

      const totalCoachingTime = sumDurations(coachingTimeMap.valueSeq());

      return {
        rep,
        coachingTimeGiven: coachingTimeMap.get(coachId) || moment.duration(0),
        coachingTimeReceived: {
          total: totalCoachingTime,
          manager: coachingTimeMap.get(coachId ? coachId : managerId) || moment.duration(0),
        },
        scoreTrend: trendFromRanks(previousScorecardRank, scorecardRank),
        scoreRank: rankData(previousScorecardRank, scorecardRank),
        activityTrend: trendFromRanks(previousActivityRank, activityRank),
        activityRank: rankData(previousActivityRank, activityRank),
        coachedBy: coachedBy(
          coachingTimeMap,
          users,
          coachIdToPlans,
          coachIdToAdHocCallsCount,
          totalCoachingTime
        ),
      };
    });
}

function sumDurations(durationSeq) {
  return durationSeq.reduce((total, item) => total.add(item), moment.duration(0));
}

function coachedBy(
  coachIdToDuration,
  userIdToUser,
  coachIdToPlans,
  coachIdToAdHocCallsCount,
  totalCoachingTime
) {
  return (
    coachIdToDuration
      .map((duration, coachId) => {
        const plans = List(coachIdToPlans[coachId]);
        const fraction = duration / totalCoachingTime;
        const user = userIdToUser.get(coachId);
        if (!user) {
          console.warn('User not found for coach', coachId);
        }
        return {
          user,
          duration,
          fraction,
          percentage: fractionToPercentage(fraction),
          plans,
          adHocCalls: coachIdToAdHocCallsCount[coachId],
        };
      })
      .valueSeq()
      // Hide coaches that look like they have 0% of the coaching due to rounding.
      .filter(data => data.percentage > 0)
      .filter(data => !!data.user)
      .sort((a, b) => b.duration - a.duration)
  );
}

export default withSorting(
  ManagerCoachingEffectivenessTable,
  props => gatherData(props),
  {
    rep: compareReps,
    coachingTimeGiven: (l, r, sortDirection) =>
      compareNumeric(
        r.coachingTimeGiven.asHours(),
        l.coachingTimeGiven.asHours(),
        sortDirection === 'asc'
      ),
    coachingTimeReceived: (l, r, sortDirection) =>
      compareNumeric(
        r.coachingTimeReceived.total.asHours(),
        l.coachingTimeReceived.total.asHours(),
        sortDirection === 'asc'
      ),
    scoreTrend: (l, r, sortDirection) =>
      compareNumeric(r.scoreTrend.trend, l.scoreTrend.trend, sortDirection === 'asc'),
    scoreRank: (l, r, sortDirection) =>
      compareRanks(l.scoreRank, r.scoreRank, sortDirection === 'asc'),
    activityTrend: (l, r, sortDirection) =>
      compareNumeric(r.activityTrend.trend, l.activityTrend.trend, sortDirection === 'asc'),
    activityRank: (l, r, sortDirection) =>
      compareRanks(l.activityRank, r.activityRank, sortDirection === 'asc'),
    coachedBy: (l, r, sortDirection) =>
      compareNumeric(r.coachedBy.size, l.coachedBy.size, sortDirection === 'asc'),
  },
  {
    sortColumn: 'coachingTimeReceived',
    sortDirection: 'desc',
  }
);
